/**
 * Report export (#39), entirely client-side.
 *
 * Excel is SheetJS, loaded dynamically like the importers so a rep who never
 * exports anything never downloads the parser. Arabic survives because xlsx is
 * UTF-8 throughout and Excel reads the sheet's own encoding — which is the
 * acceptance criterion, and the reason CSV is not offered: Excel still opens a
 * UTF-8 CSV as Windows-1252 unless the user walks through the import wizard, and
 * Arabic arrives as mojibake.
 *
 * PDF is the browser's own print-to-PDF, NOT jsPDF.
 *
 * That is a deliberate choice with a real alternative. jsPDF cannot shape Arabic:
 * it draws the isolated form of each letter, left to right, so "العملاء" comes out
 * as disconnected letters in reverse. Fixing that means shipping an Arabic-capable
 * font (hundreds of KB, against a stack whose whole premise is staying small) plus
 * a bidi/shaping layer, and the result would still be worse than what every
 * browser already does correctly and for free. The print stylesheet hides the app
 * chrome so the output is the report alone.
 */

export interface ExportColumn<T> {
  key: keyof T & string;
  header: string;
  /** For values that need formatting — money, dates, percentages. */
  format?: (row: T) => string | number;
}

/**
 * Writes an .xlsx with one sheet.
 *
 * Column widths are set explicitly: Excel defaults to 8 characters, which turns
 * every Arabic name into "####" and makes the export look broken even though the
 * data is right.
 */
export async function exportToExcel<T>(
  rows: T[],
  columns: ExportColumn<T>[],
  fileName: string,
  sheetName = 'report',
): Promise<void> {
  const XLSX = await import('xlsx');

  const body = rows.map((row) => {
    const out: Record<string, string | number> = {};
    for (const column of columns) {
      const value = column.format
        ? column.format(row)
        : (row[column.key] as unknown as string | number);
      out[column.header] = value ?? '';
    }
    return out;
  });

  const sheet = XLSX.utils.json_to_sheet(body, {
    header: columns.map((c) => c.header),
  });

  sheet['!cols'] = columns.map((column) => ({
    wch: Math.max(
      column.header.length + 2,
      ...rows.map(
        (row) => String(column.format ? column.format(row) : (row[column.key] ?? '')).length + 2,
      ),
      10,
    ),
  }));

  // Right-to-left sheet, so an Arabic report opens reading the way it was written
  // rather than with column A on the left.
  sheet['!dir'] = 'rtl';

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, sheetName.slice(0, 31));
  XLSX.writeFile(book, `${fileName}-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

/**
 * Hands the current report to the browser's print dialog, where "Save as PDF" is
 * one click and the Arabic comes out correctly shaped.
 */
export function printReport(): void {
  window.print();
}
